import { useEffect, useRef, useState } from 'react'
import { Errors } from 'config'
import { useAuthContext } from 'shared/components'

// Reauthentication renews the auth time, so a later one proves it happened
const isReauthenticatedSince = (
  requestedAtAuthTime: string,
  currentAuthTime: string
) => Date.parse(currentAuthTime) > Date.parse(requestedAtAuthTime)

// Sends a sign-in link and waits for it. The link is opened in another tab, which
// shares the session with this one: its reauthentication renews the token here
// too, with a newer auth time.
export const useReauthenticationLink = (
  onReauthenticated: () => Promise<void>
) => {
  const { auth } = useAuthContext()
  // The auth time when the link was sent, kept while the link is awaited
  const [requestedAt, setRequestedAt] = useState<string | null>(null)
  const [sendCount, setSendCount] = useState(0)
  const [isSending, setIsSending] = useState(false)
  const onReauthenticatedRef = useRef(onReauthenticated)

  useEffect(() => {
    onReauthenticatedRef.current = onReauthenticated
  })

  useEffect(() => {
    if (!auth || !requestedAt) return

    let isDone = false

    const unsubscribe = auth.onIdTokenChanged(async user => {
      const authTime = await user
        ?.getIdTokenResult()
        .then(({ authTime }) => authTime)
        .catch(() => undefined)
      if (isDone || !authTime || !isReauthenticatedSince(requestedAt, authTime))
        return

      isDone = true
      await onReauthenticatedRef.current()
    })

    return () => {
      isDone = true
      unsubscribe()
    }
  }, [auth, requestedAt])

  // Resolves with the error code, or with null once the link is sent
  const send = async (): Promise<string | null> => {
    const email = auth?.getCurrentUser()?.email
    if (!auth || !email) return Errors.NullUser

    setIsSending(true)

    try {
      // Taken before sending, so only a reauthentication after it counts
      const authTime = await auth.getAuthTime()
      if (!authTime) return Errors.NullUser
      await auth.sendSignInLinkToEmailToReauthenticate(email)
      setRequestedAt(authTime)
      setSendCount(count => count + 1)
      return null
    } catch (error: any) {
      return error?.code || Errors.InternalError
    } finally {
      setIsSending(false)
    }
  }

  // A link opened afterwards no longer continues the dialog
  const cancel = () => {
    setRequestedAt(null)
    setSendCount(0)
  }

  return {
    isWaiting: requestedAt !== null,
    isResent: sendCount > 1,
    isSending,
    send,
    cancel,
  }
}
